import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, LogIn } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Iter-259: legacy "Arrange a Viewing" widget. Used on every category
 * detail page (was inline in PropertyAdView; now shared). Two modes —
 * `appointment` (date + TIME + message REQUIRED for the API, 400 if
 * either missing) and `more_details` (message only).
 *
 * Auth-gated by contract: unauthenticated users see the sign-in nudge
 * (endpoint returns 401 otherwise). Backend already emails the seller
 * AND the admin on every successful request — zero client work there.
 *
 * The bulletin spec is a 3-step wizard (Appointment → Your Details → ✓).
 * We render Appointment + confirmation inline (Step 2 is fully derived
 * from the signed-in user on the server), so the UI stays a single card
 * with a tab switcher and a "Request sent" success state.
 */
export default function ArrangeViewingCard({ listingId, hideHeading = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("appointment");
  const [form, setForm] = useState({ preferred_date: "", preferred_time: "", message: "" });
  const [sent, setSent] = useState(false);

  const mut = useMutation({
    mutationFn: async () => {
      const body = { mode, message: form.message || undefined };
      if (mode === "appointment") {
        body.preferred_date = form.preferred_date;
        body.preferred_time = form.preferred_time;
      }
      return (await sharedApi.post(
        `/classifieds/listings/${listingId}/viewing-request`,
        body
      )).data;
    },
    onSuccess: () => {
      toast.success("Request sent — the seller has been emailed.");
      setSent(true);
      setForm({ preferred_date: "", preferred_time: "", message: "" });
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error("Please sign in to request a viewing.");
        nav(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      } else if (status === 429) {
        toast.error("Too many requests — please wait a few minutes and try again.");
      } else {
        toast.error(err?.response?.data?.detail || "Request failed");
      }
    },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!user) {
      nav(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    if (mode === "appointment" && (!form.preferred_date || !form.preferred_time)) {
      toast.error("Please pick a date and time for the appointment.");
      return;
    }
    if (mode === "more_details" && !form.message.trim()) {
      toast.error("Please share what you'd like to know.");
      return;
    }
    mut.mutate();
  };

  if (sent) {
    return (
      <div
        data-testid="arrange-viewing-card"
        className="mt-4 rounded-2xl bg-white border border-soft p-6 text-center"
      >
        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 grid place-items-center">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h2 className="font-display text-lg font-bold text-slate-800 mt-3">
          Request sent
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          They will confirm your viewing soon. Expect an email as soon as
          they respond.
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          data-testid="arrange-viewing-new"
          className="mt-4 inline-flex items-center gap-2 rounded-full text-sm font-semibold text-[color:var(--brand-blue)] hover:underline"
        >
          Request another viewing
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="arrange-viewing-card"
      className="mt-4 rounded-2xl bg-white border border-soft p-5 md:p-6"
    >
      {!hideHeading && (
        <>
          <h2 className="font-display text-lg font-bold text-slate-800">
            Arrange a viewing
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Pick a date and time — the seller will confirm.
          </p>
        </>
      )}

      <div className="flex items-center gap-2 border-b border-subtle mt-4 mb-4">
        {[
          { k: "appointment", l: "Appointment" },
          { k: "more_details", l: "More details" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setMode(t.k)}
            data-testid={`arrange-viewing-tab-${t.k}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              mode === t.k
                ? "border-[color:var(--brand-blue)] text-[color:var(--brand-blue)]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {!user && (
        <div
          data-testid="arrange-viewing-auth-gate"
          className="mb-4 flex items-start gap-2 rounded-xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] px-3 py-2 text-sm"
        >
          <LogIn className="w-4 h-4 mt-0.5" />
          <span>
            <button
              type="button"
              onClick={() =>
                nav(`/login?next=${encodeURIComponent(window.location.pathname)}`)
              }
              data-testid="arrange-viewing-signin-btn"
              className="font-bold underline"
            >
              Sign in
            </button>{" "}
            to request a viewing. Your details will be shared with the seller.
          </span>
        </div>
      )}

      <form onSubmit={submit} data-testid="arrange-viewing-form" noValidate className="space-y-3">
        {mode === "appointment" && (
          <div className="grid md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">
                Preferred date *
              </span>
              <input
                data-testid="arrange-viewing-date"
                type="date"
                value={html5FromApiDate(form.preferred_date)}
                onChange={(e) =>
                  setForm({ ...form, preferred_date: apiDateFromHtml5(e.target.value) })
                }
                className="w-full rounded-xl border border-soft bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">
                Preferred time *
              </span>
              <input
                data-testid="arrange-viewing-time"
                type="time"
                value={html5FromApiTime(form.preferred_time)}
                onChange={(e) =>
                  setForm({ ...form, preferred_time: apiTimeFromHtml5(e.target.value) })
                }
                className="w-full rounded-xl border border-soft bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--brand-blue)]"
              />
            </label>
          </div>
        )}
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">
            Message {mode === "more_details" ? "*" : "(optional)"}
          </span>
          <textarea
            data-testid="arrange-viewing-message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full rounded-xl border border-soft bg-white px-3 py-2.5 text-sm min-h-24 focus:outline-none focus:border-[color:var(--brand-blue)]"
            placeholder={
              mode === "appointment"
                ? "Kia ora — I'd like to view this listing…"
                : "Please share more details about this listing…"
            }
          />
        </label>
        <button
          type="submit"
          data-testid="arrange-viewing-submit"
          disabled={mut.isPending}
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 disabled:opacity-70 transition-colors"
        >
          {mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {mode === "appointment" ? "Request appointment" : "Request more details"}
        </button>
      </form>
    </div>
  );
}

// The backend uses DD-MM-YYYY; the <input type="date"> uses YYYY-MM-DD.
function apiDateFromHtml5(html5) {
  if (!html5) return "";
  const [y, m, d] = html5.split("-");
  return `${d}-${m}-${y}`;
}
function html5FromApiDate(api) {
  if (!api) return "";
  const [d, m, y] = api.split("-");
  return `${y}-${m}-${d}`;
}
// Backend stores 12h (`03:30 pm`); the <input type="time"> uses 24h.
function apiTimeFromHtml5(hhmm) {
  if (!hhmm) return "";
  const [hStr, m] = hhmm.split(":");
  let h = parseInt(hStr, 10);
  const suffix = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${String(h).padStart(2, "0")}:${m} ${suffix}`;
}
function html5FromApiTime(display) {
  if (!display) return "";
  const match = display.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (!match) return "";
  let h = parseInt(match[1], 10);
  const m = match[2];
  const suffix = match[3].toLowerCase();
  if (suffix === "pm" && h !== 12) h += 12;
  if (suffix === "am" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${m}`;
}
